/**
 * Size Recommender
 * Recommends a size based on customer height (cm) & weight (kg).
 * Uses weighted k-NN over real purchase data provided by the store owner.
 *
 * All sizes in the store are OVERSIZE cut. Recommendations are advisory only.
 */
(function () {
  'use strict';

  // Training data: real customer (weight kg, height cm) -> size purchased.
  // Edit/extend this list to refine recommendations over time.
  const SHIRT_DATASET = [
    { w: 55, h: 160, size: 'XXS' },
    { w: 59, h: 165, size: 'XS' },
    { w: 65, h: 175, size: 'S' },
    { w: 62, h: 169, size: 'S' },
    { w: 59, h: 170, size: 'S' },
    { w: 71, h: 172, size: 'M' },
    { w: 77, h: 182, size: 'M' },
    { w: 60, h: 177, size: 'M' },
    { w: 85, h: 180, size: 'M' },
    { w: 93, h: 190, size: 'L' },
    { w: 94, h: 178, size: 'L' },
    { w: 95, h: 180, size: 'L' },
    { w: 105, h: 193, size: 'L' },
    { w: 100, h: 177, size: 'XL' },
    { w: 97, h: 188, size: 'XL' },
    { w: 102, h: 202, size: 'XXL' }
  ];

  // Pants / shorts / jeans dataset. Numeric sizes.
  const PANTS_DATASET = [
    { w: 60, h: 165, size: '28' },
    { w: 62, h: 170, size: '28' },
    { w: 65, h: 173, size: '28' },
    { w: 58, h: 170, size: '28' },
    { w: 65, h: 175, size: '30' },
    { w: 70, h: 172, size: '30' },
    { w: 72, h: 175, size: '32' },
    { w: 80, h: 172, size: '32' },
    { w: 77, h: 182, size: '32' },
    { w: 80, h: 183, size: '32' },
    { w: 85, h: 180, size: '32' },
    { w: 105, h: 193, size: '34' },
    { w: 87, h: 195, size: '34' },
    { w: 92, h: 185, size: '34' },
    { w: 95, h: 180, size: '34' },
    { w: 97, h: 188, size: '36' },
    { w: 93, h: 190, size: '36' },
    { w: 99, h: 200, size: '38' },
    { w: 102, h: 202, size: '40' }
  ];

  const SHIRT_SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  const PANTS_SIZE_ORDER = ['28', '30', '32', '34', '36', '38', '40', '42'];
  const K = 5;

  function buildContext(dataset, sizeOrder) {
    const wMin = Math.min(...dataset.map(d => d.w));
    const wMax = Math.max(...dataset.map(d => d.w));
    const hMin = Math.min(...dataset.map(d => d.h));
    const hMax = Math.max(...dataset.map(d => d.h));
    return {
      dataset,
      sizeOrder,
      wRange: (wMax - wMin) || 1,
      hRange: (hMax - hMin) || 1
    };
  }

  const CONTEXTS = {
    shirt: buildContext(SHIRT_DATASET, SHIRT_SIZE_ORDER),
    pants: buildContext(PANTS_DATASET, PANTS_SIZE_ORDER),
    // Pants/shorts sold with letter sizing — use pants dataset, then convert.
    'pants-letter': Object.assign(
      buildContext(PANTS_DATASET, SHIRT_SIZE_ORDER),
      { convert: 'numericToLetter' }
    )
  };

  // Map a numeric pants size to its letter equivalent based on the store's
  // size chart (XS=28/30, S=30/32, M=32/34, L=34/36, XL=36/38, XXL=38/40).
  const NUMERIC_TO_LETTER = {
    '28': 'XS',
    '30': 'S',
    '32': 'M',
    '34': 'L',
    '36': 'XL',
    '38': 'XXL',
    '40': 'XXL'
  };

  // Reverse of the above, for products that recommend a letter but sell numeric.
  const LETTER_TO_NUMERIC = {
    'XXS': '28',
    'XS': '28',
    'S': '30',
    'M': '32',
    'L': '34',
    'XL': '36',
    'XXL': '38',
    'XXXL': '40'
  };

  // Inspect the product's actual size options and decide whether they use
  // letter sizing (S/M/L) or numeric sizing (28/30/32). Returns 'letter',
  // 'numeric', or null when it can't tell.
  function detectSizeSystem(sizes) {
    if (!sizes || sizes.length === 0) return null;
    let letter = 0;
    let numeric = 0;
    sizes.forEach(s => {
      const norm = String(s).toUpperCase().trim();
      if (SHIRT_SIZE_ORDER.indexOf(norm) !== -1) letter += 1;
      else if (/^\d+$/.test(norm)) numeric += 1;
    });
    if (letter === 0 && numeric === 0) return null;
    return letter >= numeric ? 'letter' : 'numeric';
  }

  // Convert a recommended size into whatever system the product actually sells,
  // and return the matching size order so snapping stays accurate. This makes
  // the recommender self-correcting instead of relying on a title whitelist.
  function matchToAvailableSystem(size, availableSizes, ctx) {
    const sizeIsNumeric = /^\d+$/.test(String(size).trim());
    const system = detectSizeSystem(availableSizes);

    // Couldn't detect the product's sizes — fall back to the context's hint.
    if (!system) {
      if (ctx.convert === 'numericToLetter' && sizeIsNumeric) {
        return { size: NUMERIC_TO_LETTER[String(size)] || size, sizeOrder: SHIRT_SIZE_ORDER };
      }
      return { size: size, sizeOrder: ctx.sizeOrder };
    }

    if (system === 'letter') {
      if (sizeIsNumeric) {
        return { size: NUMERIC_TO_LETTER[String(size)] || size, sizeOrder: SHIRT_SIZE_ORDER };
      }
      return { size: size, sizeOrder: SHIRT_SIZE_ORDER };
    }

    // system === 'numeric'
    if (!sizeIsNumeric) {
      return { size: LETTER_TO_NUMERIC[String(size).toUpperCase()] || size, sizeOrder: PANTS_SIZE_ORDER };
    }
    return { size: size, sizeOrder: PANTS_SIZE_ORDER };
  }

  function recommend(weight, height, ctx) {
    const wn = weight / ctx.wRange;
    const hn = height / ctx.hRange;

    const scored = ctx.dataset.map(d => {
      const dw = wn - d.w / ctx.wRange;
      const dh = hn - d.h / ctx.hRange;
      return { size: d.size, dist: Math.sqrt(dw * dw + dh * dh) };
    }).sort((a, b) => a.dist - b.dist);

    const top = scored.slice(0, K);
    const votes = {};
    top.forEach(n => {
      const weight = 1 / (n.dist + 0.001);
      votes[n.size] = (votes[n.size] || 0) + weight;
    });

    let best = null;
    let bestScore = -Infinity;
    Object.keys(votes).forEach(size => {
      if (votes[size] > bestScore) {
        bestScore = votes[size];
        best = size;
      }
    });
    return best;
  }

  // Snap the recommended size to the closest one actually offered by the product.
  function snapToAvailable(size, availableSizes, sizeOrder) {
    if (!availableSizes || availableSizes.length === 0) return size;
    const normalize = s => String(s).toUpperCase().trim();
    const available = availableSizes.map(normalize);
    if (available.includes(normalize(size))) return size;

    const targetIdx = sizeOrder.indexOf(normalize(size));
    if (targetIdx === -1) return size;

    let closest = null;
    let closestDist = Infinity;
    availableSizes.forEach(s => {
      const idx = sizeOrder.indexOf(normalize(s));
      if (idx === -1) return;
      const d = Math.abs(idx - targetIdx);
      if (d < closestDist) {
        closestDist = d;
        closest = s;
      }
    });
    // None of the available sizes were in this size order (wrong system) —
    // return the recommendation unchanged rather than defaulting to the first.
    return closest === null ? size : closest;
  }

  class SizeRecommender extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-sr-form]');
      this.resultEl = this.querySelector('[data-sr-result]');
      this.resultSizeEl = this.querySelector('[data-sr-result-size]');
      this.resultNoteEl = this.querySelector('[data-sr-result-note]');
      this.applyBtn = this.querySelector('[data-sr-apply]');
      this.againBtn = this.querySelector('[data-sr-again]');
      this.submitBtn = this.querySelector('[data-sr-submit]');
      this.weightInput = this.querySelector('[data-sr-weight]');
      this.heightInput = this.querySelector('[data-sr-height]');

      if (!this.form || !this.submitBtn) return;

      this.submitBtn.addEventListener('click', this.onSubmit.bind(this));

      const onKey = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.onSubmit(e);
        }
      };
      if (this.weightInput) this.weightInput.addEventListener('keydown', onKey);
      if (this.heightInput) this.heightInput.addEventListener('keydown', onKey);

      if (this.againBtn) {
        this.againBtn.addEventListener('click', this.reset.bind(this));
      }
      if (this.applyBtn) {
        this.applyBtn.addEventListener('click', this.applySize.bind(this));
      }
    }

    getContext() {
      const type = (this.dataset.sizeType || 'shirt').toLowerCase();
      return CONTEXTS[type] || CONTEXTS.shirt;
    }

    onSubmit(e) {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      const weight = parseFloat(this.weightInput.value);
      const height = parseFloat(this.heightInput.value);

      if (!weight || !height || weight < 30 || weight > 250 || height < 120 || height > 230) {
        if (!weight || weight < 30 || weight > 250) this.weightInput.focus();
        else this.heightInput.focus();
        return;
      }

      const ctx = this.getContext();
      const availableSizes = this.getAvailableSizes();
      let size = recommend(weight, height, ctx);

      // Convert the recommendation to whatever system the product actually
      // sells (letters vs numbers) and snap using the matching size order.
      const matched = matchToAvailableSystem(size, availableSizes, ctx);
      size = matched.size;
      const sizeOrder = matched.sizeOrder;
      size = snapToAvailable(size, availableSizes, sizeOrder);

      // Super-oversize products: recommend one size smaller.
      if (this.dataset.superOversize === 'true') {
        size = stepDown(size, availableSizes, sizeOrder);
      }

      this.showResult(size);
    }

    showResult(size) {
      if (this.resultSizeEl) this.resultSizeEl.textContent = size;
      this.form.hidden = true;
      this.resultEl.hidden = false;
    }

    reset() {
      this.resultEl.hidden = true;
      this.form.hidden = false;
      this.weightInput.value = '';
      this.heightInput.value = '';
      this.heightInput.focus();
    }

    getAvailableSizes() {
      const optionName = (this.dataset.sizeOption || '').toLowerCase();
      if (!optionName) return [];

      // Look in the surrounding product form for a size selector.
      const productSection = document.getElementById(this.dataset.sectionId) || document;
      const selectors = productSection.querySelectorAll('[data-option]');
      let target = null;
      selectors.forEach(el => {
        if (el.getAttribute('data-option').toLowerCase() === optionName) target = el;
      });
      if (!target) return [];

      const values = new Set();
      target.querySelectorAll('input.js-option').forEach(input => {
        if (input.value) values.add(input.value);
      });
      target.querySelectorAll('option').forEach(opt => {
        if (opt.value) values.add(opt.value);
      });
      return Array.from(values);
    }

    applySize() {
      const size = this.resultSizeEl ? this.resultSizeEl.textContent.trim() : '';
      if (!size) return;
      const optionName = (this.dataset.sizeOption || '').toLowerCase();
      const productSection = document.getElementById(this.dataset.sectionId) || document;
      const selectors = productSection.querySelectorAll('[data-option]');
      let target = null;
      selectors.forEach(el => {
        if (el.getAttribute('data-option').toLowerCase() === optionName) target = el;
      });
      if (!target) return;

      // Radio button style
      let applied = false;
      target.querySelectorAll('input.js-option').forEach(input => {
        if (!applied && input.value === size && !input.disabled) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          applied = true;
        }
      });
      // Dropdown / custom-select style
      if (!applied) {
        const select = target.querySelector('select');
        if (select) {
          select.value = size;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          applied = true;
        }
      }

      // Close the modal we live in.
      const modal = this.closest('modal-dialog');
      if (modal && typeof modal.hide === 'function') {
        modal.hide();
      } else if (modal) {
        const closeBtn = modal.querySelector('.js-close-modal');
        if (closeBtn) closeBtn.click();
      }
    }
  }

  function stepDown(size, availableSizes, sizeOrder) {
    const idx = sizeOrder.indexOf(String(size).toUpperCase());
    if (idx <= 0) return size;
    const smaller = sizeOrder[idx - 1];
    if (!availableSizes || availableSizes.length === 0) return smaller;
    return snapToAvailable(smaller, availableSizes, sizeOrder);
  }

  if (!customElements.get('size-recommender')) {
    customElements.define('size-recommender', SizeRecommender);
  }
})();
